import { t, Trans } from "@lingui/macro";
import { ApiEpBilling } from "@smp/api";
import { UiButton, UiDialogResponsive, UiTextFieldV2 } from "@smp/ui";
import { IconShare } from "@tabler/icons-react";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { FC, useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { useUpdateEffect } from "react-use";

import { useDialog } from "@/hooks/use_dialog";

import classes from "./embed_button.module.css";

export const EmbedButton: FC<{
  disabled: boolean;
}> = ({ disabled }) => {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const { isOpen, open, close } = useDialog();

  const {
    values: settings,
    handleChange: handleChangeSettings,
    resetForm: resetSettings,
  } = useFormik<{
    width: number;
    height: number;
  }>({
    initialValues: {
      width: 546,
      height: 406,
    },
    onSubmit: () => {},
  });

  const { data: affiliate } = useQuery(
    "ep.billing.affiliate",
    ApiEpBilling.Affiliate.retrieve,
    {
      enabled: isOpen,
    },
  );

  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return `${window.location.origin}/home/embed/templates/${
      params.size ? "?" + params.toString() : ""
    }`;
  }, [location.search]);

  const iframeCode = useMemo(() => {
    const url = new URL(iframeSrc);

    if (affiliate?.custom_code) {
      url.searchParams.append("partner", affiliate?.custom_code);
    }

    return `<iframe width="${settings.width}" height="${settings.height}" src="${url}" title="SMMplanner templates" frameBorder="0" referrerPolicy="strict-origin-when-cross-origin"></iframe>`;
  }, [affiliate?.custom_code, iframeSrc, settings.height, settings.width]);

  const handleCopyCode = useCallback(() => {
    navigator.clipboard.writeText(iframeCode).then(() => {
      enqueueSnackbar(t`Copied to clipboard`, {
        variant: "success",
      });
    });
  }, [enqueueSnackbar, iframeCode]);

  useUpdateEffect(() => {
    if (!isOpen) {
      resetSettings();
    }
  }, [isOpen]);

  return (
    <>
      <UiButton
        startIcon={<IconShare />}
        className={classes.button}
        onClick={open}
        disabled={disabled}
      >
        <Trans>Share</Trans>
      </UiButton>

      <UiDialogResponsive
        open={isOpen}
        onClose={close}
        maxWidth={940}
        fullWidth
        isButtonCloseHidden
      >
        <div className={classes.content}>
          <div className={classes.iframe}>
            <iframe
              className={classes.iframe__iframe}
              src={iframeSrc}
              title="SMMplanner templates"
              frameBorder="0"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>

          <div className={classes.sidebar}>
            <UiTextFieldV2
              variant="outlined"
              value={settings.width}
              name="width"
              onChange={handleChangeSettings}
              fullWidth
              type="number"
              validationEnabled
              inputProps={{
                min: 100,
              }}
              label={<Trans>Width</Trans>}
            />

            <UiTextFieldV2
              variant="outlined"
              value={settings.height}
              name="height"
              onChange={handleChangeSettings}
              fullWidth
              type="number"
              validationEnabled
              inputProps={{
                min: 100,
              }}
              label={<Trans>Height</Trans>}
            />

            <UiTextFieldV2
              variant="outlined"
              value={iframeCode}
              onChange={handleChangeSettings}
              fullWidth
              multiline
              inputProps={{
                readOnly: true,
                min: 100,
              }}
              label={<Trans>Code for embedding on the site</Trans>}
            />

            <div className={classes.sidebar__buttons}>
              <UiButton variant="semifilled" fullWidth onClick={close}>
                <Trans>Close</Trans>
              </UiButton>

              <UiButton variant="contained" fullWidth onClick={handleCopyCode}>
                <Trans>Copy code</Trans>
              </UiButton>
            </div>
          </div>
        </div>
      </UiDialogResponsive>
    </>
  );
};
